// The account-wide overview wire shapes — `GET /v1/sessions` (the cross-repo
// activity feed) and `GET /v1/approvals` (the human-decision queue), both also
// re-emitted on `/v1/streams` as the `sessions` and `approvals` topics — plus
// `GET /v1/approved-prs` (the pull requests a maintainer has APPROVED, which the
// approvals queue does NOT serve: that one lists the asks still parked on a
// human decision) and the two headline reads, `GET /v1/overview/counts` and
// `GET /v1/overview/burn` (at the bottom of this file, with the windows they
// count over).
//
// ## `entity_title` MEANS TWO DIFFERENT THINGS on `/v1`, and both are shipped
//
// This is the discrepancy the wire layer was supposed to surface, and it is not
// one that can be fixed here — both bodies are published:
//
// | Body | `entity_title` is |
// |---|---|
// | `GET /v1/sessions` (this file) | the DERIVED headline: issue title, else `PR #<n>`, else the raw trigger |
// | `GET /v1/sessions/:id` (`session.ts`) | the RAW issue title, or null — no fallback |
//
// So a client that reads the feed and then point-reads a row sees
// `entity_title` change from `PR #77` to `null` for the same session, with
// nothing wrong. The derived headline is ALSO published as `title` on the
// repo-scoped list row (`SessionSummaryWire`), which is the name it should have
// had here. Renaming either is a breaking change for a paying client, so the
// two meanings are STATED rather than silently reconciled — and a reader of
// either resource now finds the other named beside it.
//
// The two session shapes differ elsewhere too, and deliberately: this row
// carries `repo_full_name` (it is cross-repo, the repo-scoped list already
// knows), and does NOT carry `model`.

import { z } from 'zod';
import { type MergeAskCauseKind, MergeAskCauseKindSchema } from '../entities/issue';
import {
  STORED_MERGE_REFUSAL_CODES,
  type StoredMergeRefusalCode,
} from '../value-objects/merge-refusal-posture';
import { defineResource } from './resource';

/** The camelCase row the account-wide feed reads. */
export type OverviewSessionEntity = {
  id: string;
  repoFullName: string;
  trigger: string;
  entityKind: string;
  outcome: string | null;
  startedAt: Date;
  finishedAt: Date | null;
  issueTitle: string | null;
  issueNumber: number | null;
  prNumber: number | null;
};

/** One PR parked on `ask` — the human-decision queue item. */
export type OverviewApprovalEntity = {
  id: string;
  repoFullName: string;
  githubNumber: number;
  /** Cached PR title; `''` when the webhook never carried one. */
  title: string;
  authorLogin: string | null;
  ciStatus: string;
  lastSessionId: string | null;
  createdAt: Date;
  /** The head the ask is ABOUT — what an approval is of (plan 101 M2). Null before a push was seen. */
  headSha: string | null;
  /**
   * The re-ask record (#5671): why the row is back on `ask`, and the head the
   * voided approval covered. Null when nothing is recorded.
   */
  mergeAskCause: MergeAskCauseKind | null;
  mergeAskPriorHeadSha: string | null;
};

/** The feed row. Field names and nullability are the existing contract. */
export const OverviewSessionWireSchema = z.object({
  id: z.string(),
  repo_full_name: z.string(),
  trigger: z.string(),
  entity_kind: z.string(),
  /** GitHub number of the triggering issue/PR; null when neither resolved. */
  entity_number: z.number().nullable(),
  /**
   * The DERIVED headline — issue title, else `PR #<n>`, else the raw trigger.
   * NOT the same field as `SessionDetailWire.entity_title`; see the header.
   * Never null: the trigger is always a string, so the fallback always lands.
   */
  entity_title: z.string(),
  /** Null while the session is still running. */
  outcome: z.string().nullable(),
  started_iso: z.string(),
  finished_iso: z.string().nullable(),
});

/** What a client should type against for an overview session row. */
export type OverviewSessionWire = z.infer<typeof OverviewSessionWireSchema>;

/** The approvals-queue row. */
export const OverviewApprovalWireSchema = z.object({
  id: z.string(),
  repo_full_name: z.string(),
  github_number: z.number(),
  /** Null (not `''`) for a never-seen title, so clients fall back cleanly (#852). */
  title: z.string().nullable(),
  author_login: z.string().nullable(),
  ci_status: z.string(),
  last_session_id: z.string().nullable(),
  opened_iso: z.string(),
  /**
   * The PR head this ask is about. `POST /v1/prs/:id/{approve,skip}-pending`
   * takes it back as `head_sha`: a decision is OF a head, and a push since
   * answers 409 `head_moved` (plan 101 M2). Null until a head is seen — such a
   * PR cannot be decided yet (`head_unknown`).
   */
  head_sha: z.string().nullable(),
  /**
   * WHY the row is back on `ask` (#5671) — a member of the domain's
   * `MergeAskCauseKind` list, the vocabulary `markPullRequestMergeAsk` stores.
   * Null when nothing is recorded.
   */
  merge_ask_cause: MergeAskCauseKindSchema.nullable(),
  /** The head the voided approval covered, as the stamp recorded it. Null when nothing is recorded. */
  merge_ask_prior_head_sha: z.string().nullable(),
});

/** What a client should type against for an approvals-queue row. */
export type OverviewApprovalWire = z.infer<typeof OverviewApprovalWireSchema>;

export const OverviewSessionResource = defineResource<
  OverviewSessionEntity,
  typeof OverviewSessionWireSchema
>({
  schema: OverviewSessionWireSchema,
  serialize: (s) => ({
    id: s.id,
    repo_full_name: s.repoFullName,
    trigger: s.trigger,
    entity_kind: s.entityKind,
    entity_number: s.issueNumber ?? s.prNumber,
    // `||`, not `??` — `issues.title` defaults to `''` on a pre-#980 row, and a
    // blank heading is worse than the PR number it falls back to.
    entity_title: s.issueTitle || (s.prNumber !== null ? `PR #${s.prNumber}` : s.trigger),
    outcome: s.outcome,
    started_iso: s.startedAt.toISOString(),
    finished_iso: s.finishedAt ? s.finishedAt.toISOString() : null,
  }),
});

export const OverviewApprovalResource = defineResource<
  OverviewApprovalEntity,
  typeof OverviewApprovalWireSchema
>({
  schema: OverviewApprovalWireSchema,
  serialize: (p) => ({
    id: p.id,
    repo_full_name: p.repoFullName,
    github_number: p.githubNumber,
    title: p.title || null,
    author_login: p.authorLogin,
    ci_status: p.ciStatus,
    last_session_id: p.lastSessionId,
    opened_iso: p.createdAt.toISOString(),
    head_sha: p.headSha,
    merge_ask_cause: p.mergeAskCause,
    merge_ask_prior_head_sha: p.mergeAskPriorHeadSha,
  }),
});

/**
 * One OPEN pull request a maintainer has APPROVED for merging — the row
 * `GET /v1/approved-prs` serves.
 *
 * The approvals queue beside it answers a different question (a PR waiting for a
 * human to decide); this one answers "what has already been approved, and is it
 * still mergeable". Hence the waiting facts it carries: CI, mergeability,
 * GitHub's own block, and the refusal the STORED row proves.
 */
export type ApprovedPrEntity = {
  id: string;
  repoFullName: string;
  githubNumber: number;
  /** Cached PR title; null when the webhook never carried one. */
  title: string | null;
  authorLogin: string | null;
  /** When the row was created — the PR's age on this surface. */
  openedAt: Date;
  /**
   * The instant the approval was recorded (`merge_decided_at`). Null when the
   * row carries no decision stamp — an approval predating the column reads null
   * rather than a guessed instant.
   */
  approvedAt: Date | null;
  /** The stored CI grade of the live head. */
  ciStatus: string;
  /** GitHub's tri-state conflict test: `mergeable` | `conflicting` | `unknown`. */
  mergeable: string | null;
  /** GitHub's own refusal to take the merge: `blocked` | `behind` | `draft`. */
  mergeBlockState: string | null;
  /** The one sentence naming who holds GitHub's block. */
  mergeBlockReason: string | null;
  /** Whether GitHub's own auto-merge is armed on the PR. */
  autoMerge: boolean;
  /**
   * The refusal the STORED row proves — `head_moved` (the approval is pinned to
   * a head that is no longer the live one) or `github_blocks` (GitHub's own
   * stored block) — and null when the row proves neither, in which case the
   * reader shows the row's live waiting state instead.
   */
  mergeRefusalCode: StoredMergeRefusalCode | null;
  /**
   * Our review of the live head, still posting (plan 102 slice 05): `posted` of `total`
   * comments are up. Null when no outbox of ours is posting for that head.
   */
  reviewPosting: { posted: number; total: number } | null;
};

/**
 * The approved-open pull request row.
 *
 * `merge_refusal_code` is the ONE derived field on this body, and it is derived
 * from the stored row alone (`storedMergeRefusalCode`), never from the audit
 * trail: the pull-request table carries no refusal-code column.
 */
export const ApprovedPrWireSchema = z.object({
  id: z.string(),
  repo_full_name: z.string(),
  github_number: z.number(),
  /** Null (not `''`) for a never-seen title, so clients fall back cleanly (#852). */
  title: z.string().nullable(),
  author_login: z.string().nullable(),
  opened_iso: z.string(),
  /** The instant the approval was recorded; null when the row carries no stamp. */
  approved_iso: z.string().nullable(),
  ci_status: z.string(),
  mergeable: z.string().nullable(),
  merge_block_state: z.string().nullable(),
  merge_block_reason: z.string().nullable(),
  auto_merge: z.boolean(),
  /** The refusal the stored row proves, or null when it proves none. */
  merge_refusal_code: z.enum(STORED_MERGE_REFUSAL_CODES).nullable(),
  /** Our review of the live head still posting, k of N; null when none is posting. */
  review_posting: z
    .object({ posted: z.number().int().nonnegative(), total: z.number().int().nonnegative() })
    .nullable(),
});

/** What a client should type against for an approved-open pull request row. */
export type ApprovedPrWire = z.infer<typeof ApprovedPrWireSchema>;

export const ApprovedPrResource = defineResource<ApprovedPrEntity, typeof ApprovedPrWireSchema>({
  schema: ApprovedPrWireSchema,
  serialize: (p) => ({
    id: p.id,
    repo_full_name: p.repoFullName,
    github_number: p.githubNumber,
    title: p.title || null,
    author_login: p.authorLogin,
    opened_iso: p.openedAt.toISOString(),
    approved_iso: p.approvedAt ? p.approvedAt.toISOString() : null,
    ci_status: p.ciStatus,
    mergeable: p.mergeable,
    merge_block_state: p.mergeBlockState,
    merge_block_reason: p.mergeBlockReason,
    auto_merge: p.autoMerge,
    merge_refusal_code: p.mergeRefusalCode,
    review_posting: p.reviewPosting === null ? null : { ...p.reviewPosting },
  }),
});

// ─── the headline reads: counts + burn ───────────────────────────────────────
//
// Each body carries the START of the window it counted over. That instant is
// the one the query bounded its rows with (`accountOverviewCounts` /
// `accountBurn`, `packages/db/src/queries/overview-counts.ts`, which own both
// windows) and is carried through verbatim, never re-derived here: a second
// statement of either window is a `*_since_iso` free to disagree with the rows.
// The counts window is the standup day (`standupDayStart`,
// `value-objects/standup-day.ts`): it starts at the most recent 14:00 UTC, not
// 24 hours back, despite the `merged_24h` name the public contract keeps.

const CountSchema = z.number().int().nonnegative();

/** The account's headline counts, as the query answers them. */
export type OverviewCountsEntity = {
  reposEnabled: number;
  openPrs: number;
  merged24h: number;
  pendingApprovals: number;
  /**
   * Inclusive start of the `merged24h` window, as the query bounded it: the most
   * recent 14:00 UTC (22:00 Asia/Manila) at or before the read.
   */
  mergedSince: Date;
};

export const OverviewCountsWireSchema = z.object({
  repos_enabled: CountSchema.describe('Repositories this account has enabled.'),
  open_prs: CountSchema.describe('Open pull requests across the enabled repositories.'),
  merged_24h: CountSchema.describe(
    'Pull requests on the enabled repositories merged at or after `merged_since_iso` (the most recent 14:00 UTC, not a rolling 24 hours, despite the name).',
  ),
  pending_approvals: CountSchema.describe(
    'Pull requests on the enabled repositories parked on an ask-before-acting decision: the full size of the queue `GET /v1/approvals` lists the head of.',
  ),
  merged_since_iso: z
    .string()
    .describe(
      'Inclusive start of the `merged_24h` window (ISO 8601): the most recent 14:00 UTC (22:00 Asia/Manila) at or before the read, the cutoff the daily team standup counts from. Not a rolling 24 hours: the window is between 0 and 24 hours long.',
    ),
});

export type OverviewCountsWire = z.infer<typeof OverviewCountsWireSchema>;

export const OverviewCountsResource = defineResource<
  OverviewCountsEntity,
  typeof OverviewCountsWireSchema
>({
  schema: OverviewCountsWireSchema,
  serialize: (c) => ({
    repos_enabled: c.reposEnabled,
    open_prs: c.openPrs,
    merged_24h: c.merged24h,
    pending_approvals: c.pendingApprovals,
    merged_since_iso: c.mergedSince.toISOString(),
  }),
});

/** The account's recorded session spend over the burn window, as the query answers it. */
export type OverviewBurnEntity = {
  /**
   * `numeric` sum as Postgres renders it, or null when no displayable cost exists
   * in the window. The server never coerces null to `'0'`: whether a zero may be
   * SHOWN depends on `sessions` (the wire description states the rule), and a
   * `'0'` here would erase that distinction (#2485).
   */
  costUsd: string | null;
  sessions: number;
  unpriced: number;
  /** Inclusive start of the window, as the query bounded it. */
  since: Date;
};

export const OverviewBurnWireSchema = z.object({
  cost_usd: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .nullable()
    .describe(
      'Recorded USD cost of the sessions started at or after `since_iso` whose price may be shown as a cost, as a decimal string. ' +
        'On your own keys this is the recorded estimate, not a bill. Null when no displayable cost exists in the window. ' +
        'A client MAY render null as zero only when `sessions` is 0 (nothing ran, a measured zero); when `sessions` is ' +
        'greater than 0 it must not, because sessions ran and none carried a displayable price.',
    ),
  sessions: CountSchema.describe('Agent sessions the account started at or after `since_iso`.'),
  unpriced: CountSchema.describe(
    'How many of `sessions` spent tokens but carry no price that may be shown as a cost (unpriced, a fail-safe ceiling, or unattributed legacy), and so are absent from `cost_usd`.',
  ),
  since_iso: z
    .string()
    .describe('Inclusive start of the window (ISO 8601): one hour before the read was taken.'),
});

export type OverviewBurnWire = z.infer<typeof OverviewBurnWireSchema>;

export const OverviewBurnResource = defineResource<
  OverviewBurnEntity,
  typeof OverviewBurnWireSchema
>({
  schema: OverviewBurnWireSchema,
  serialize: (b) => ({
    cost_usd: b.costUsd,
    sessions: b.sessions,
    unpriced: b.unpriced,
    since_iso: b.since.toISOString(),
  }),
});
